import {
  renderJsonPreview,
  renderXmlPreview,
  renderCsvPreview,
  renderFixedWidthPreview,
  type ExportStructureNode,
  type ExportDestinationContent,
  type SampleBatch,
} from '@capture/shared';
import type { ExportColumn } from '../../../hooks/useExportFormats';
import { buildSpecByColumn } from '../spec-by-column';
import styles from './StructurePreviewPane.module.css';

type Mapping = ExportDestinationContent['mappings'][number];

interface Props {
  mappings: Mapping[];
  structure: ExportStructureNode | undefined;
  format: 'json' | 'xml' | 'csv' | 'fixed-width';
  sampleData: SampleBatch;
  /** Required for fixed-width: per-targetColumn widths. */
  widths?: Record<string, number>;
  /**
   * The selected format's columns (with typed `format` specs). When provided,
   * the FLAT preview path applies column-level format specs via the same
   * `resolveEffectiveColumns` the worker uses. The structured-output path
   * (when `structure` is set) does not yet thread specs — tracked follow-up.
   * (EX-2/F-053.)
   */
  columns?: ExportColumn[];
}

const FORMAT_LABELS: Record<Props['format'], string> = {
  json: 'JSON',
  xml: 'XML',
  csv: 'CSV',
  'fixed-width': 'Fixed-width',
};

export function StructurePreviewPane({ mappings, structure, format, sampleData, widths, columns }: Props) {
  const specByColumn = buildSpecByColumn(columns, mappings);
  let output: string;
  try {
    switch (format) {
      case 'json':
        output = renderJsonPreview(mappings, sampleData, structure, specByColumn);
        break;
      case 'xml':
        output = renderXmlPreview(mappings, sampleData, structure, specByColumn);
        break;
      case 'csv':
        output = renderCsvPreview(mappings, sampleData, specByColumn);
        break;
      case 'fixed-width':
        output = renderFixedWidthPreview(mappings, sampleData, widths ?? {}, specByColumn);
        break;
    }
  } catch (err) {
    output = `Preview error: ${err instanceof Error ? err.message : String(err)}`;
  }

  return (
    <div className={styles.pane}>
      <header className={styles.header}>
        <span className={styles.label}>Live preview</span>
        <span className={styles.formatChip} data-testid="structure-preview-format">{FORMAT_LABELS[format]}</span>
      </header>
      <pre className={styles.output} data-testid="structure-preview-pre">
        {output}
      </pre>
    </div>
  );
}
